import React, { useState, useEffect } from 'react';
import {
    Typography,
    Container,
    Grid,
    Card,
    CardContent,
    Button,
    Box,
    Chip,
    CircularProgress,
    Alert,
    Tabs,
    Tab,
    Paper,
    Pagination,
    Breadcrumbs,
    Link
} from '@mui/material';
import {
    ElectricBolt as HeatPumpIcon,
    Home as InsulationIcon,
    SolarPower as PVIcon,
    BatteryChargingFull as BESSIcon,
    Window as WindowIcon,
    Home as HomeIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const DecarbonizationActions = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [categories, setCategories] = useState({
        heatPumps: [],
        insulations: [],
        pvSystems: [],
        pvBessSystems: [],
        windows: []
    });

    const itemsPerPage = 12;

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [heatPumpsRes, insulationsRes, pvRes, pvBessRes, windowsRes] = await Promise.all([
                    axios.get('/heat-pumps/'),
                    axios.get('/insulations/'),
                    axios.get('/pvs/'),
                    axios.get('/pvbess/'),
                    axios.get('/windows/')
                ]);

                setCategories({
                    heatPumps: heatPumpsRes.data,
                    insulations: insulationsRes.data,
                    pvSystems: pvRes.data,
                    pvBessSystems: pvBessRes.data,
                    windows: windowsRes.data
                });
                setLoading(false);
            } catch (err) {
                console.error('Error fetching decarbonization actions:', err);
                setError(true);
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    const categoryData = [
        {
            name: 'Heat Pumps',
            icon: <HeatPumpIcon sx={{ fontSize: '2.2rem' }} />,
            color: '#1976d2',
            data: categories.heatPumps,
            description: 'High-efficiency heating and cooling systems that can significantly reduce energy consumption.'
        },
        {
            name: 'Insulation Materials',
            icon: <InsulationIcon sx={{ fontSize: '2.2rem' }} />,
            color: '#388e3c',
            data: categories.insulations,
            description: 'Thermal insulation solutions to improve building envelope performance and reduce heat loss.'
        },
        {
            name: 'Solar PV Systems',
            icon: <PVIcon sx={{ fontSize: '2.2rem' }} />,
            color: '#f57c00',
            data: categories.pvSystems,
            description: 'Photovoltaic systems to generate clean electricity and reduce grid dependency.'
        },
        {
            name: 'PV + Battery Systems',
            icon: <BESSIcon sx={{ fontSize: '2.2rem' }} />,
            color: '#7b1fa2',
            data: categories.pvBessSystems,
            description: 'Combined solar and battery storage systems for energy independence and backup power.'
        },
        {
            name: 'Windows',
            icon: <WindowIcon sx={{ fontSize: '2.2rem' }} />,
            color: '#d32f2f',
            data: categories.windows,
            description: 'Advanced glazing solutions to minimize heat transfer and improve thermal comfort.'
        }
    ];

    const handleCategoryChange = (event, newValue) => {
        setSelectedCategory(newValue);
        setCurrentPage(1);
    };

    const handlePageChange = (event, value) => {
        setCurrentPage(value);
    };

    if (loading) {
        return (
            <Container sx={{ mt: 5, textAlign: 'center' }}>
                <CircularProgress />
                <Typography variant="h6" sx={{ mt: 2 }}>Loading decarbonization actions...</Typography>
            </Container>
        );
    }

    if (error) {
        return (
            <Container sx={{ mt: 5 }}>
                <Alert severity="error">Failed to load decarbonization actions. Please try again later.</Alert>
            </Container>
        );
    }

    const selectedCategoryData = categoryData[selectedCategory];
    // Sort data by ID in ascending order
    const sortedData = [...selectedCategoryData.data].sort((a, b) => a.id - b.id);
    const totalPages = Math.ceil(sortedData.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentItems = sortedData.slice(startIndex, endIndex);

    return (
        <Container maxWidth="xl" sx={{ mt: 5, mb: 5 }}>
            {/* Breadcrumbs */}
            <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 3 }}>
                <Link
                    underline="hover"
                    color="inherit"
                    href="#"
                    onClick={(e) => {
                        e.preventDefault();
                        navigate('/');
                    }}
                    sx={{ display: 'flex', alignItems: 'center' }}
                >
                    <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                    Home
                </Link>
                <Typography color="text.primary">Decarbonization Actions</Typography>
            </Breadcrumbs>

            {/* Title and Subtitle with styled box */}
            <Box sx={{
                mb: 4,
                p: 4,
                borderRadius: 3,
                background: 'linear-gradient(135deg, #f5f9f6 0%, #e8f5e8 100%)',
                border: '1px solid',
                borderColor: 'divider'
            }}>
                <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 2, color: 'primary.main' }}>
                    Decarbonization Actions
                </Typography>
                <Typography variant="h6" sx={{ color: 'text.secondary', maxWidth: 800 }}>
                    Explore various sustainable solutions to reduce your building's carbon footprint.
                    Each category offers different technologies and approaches to improve energy efficiency and sustainability.
                </Typography>
            </Box>

            {/* Category Tabs - Centered */}
            <Paper elevation={2} sx={{ mb: 4 }}>
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <Tabs
                        value={selectedCategory}
                        onChange={handleCategoryChange}
                        variant="scrollable"
                        scrollButtons="auto"
                        sx={{
                            '& .MuiTab-root': {
                                minHeight: 80,
                                fontSize: '1rem',
                                fontWeight: 'bold'
                            }
                        }}
                    >
                        {categoryData.map((category, index) => (
                            <Tab
                                key={index}
                                label={
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                        <Box sx={{ color: category.color, mb: 1 }}>
                                            {category.icon}
                                        </Box>
                                        <Typography variant="body2">{category.name}</Typography>
                                    </Box>
                                }
                            />
                        ))}
                    </Tabs>
                </Box>
            </Paper>

            {/* Selected Category Content */}
            <Box sx={{ mb: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Box sx={{ color: selectedCategoryData.color, mr: 2 }}>
                        {selectedCategoryData.icon}
                    </Box>
                    <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                        {selectedCategoryData.name}
                    </Typography>
                </Box>
                <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary', maxWidth: 800 }}>
                    {selectedCategoryData.description}
                </Typography>
            </Box>

            {/* Action Cards Grid */}
            <Grid container spacing={3}>
                {currentItems.map((item, index) => (
                    <Grid item xs={12} sm={6} md={4} key={index}>
                        <Card
                            elevation={6}
                            sx={{
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                transition: 'box-shadow 0.3s, transform 0.2s',
                                borderRadius: 4,
                                background: 'linear-gradient(135deg, #ffffff 0%, #f5faff 100%)',
                                '&:hover': {
                                    boxShadow: 12,
                                    transform: 'translateY(-4px)'
                                }
                            }}
                        >
                            <CardContent sx={{ flexGrow: 1, pb: 3 }}>
                                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                                    {item.name || `System ${item.id}`}
                                </Typography>

                                {/* Dynamic properties based on category */}
                                {selectedCategory === 0 && ( // Heat Pumps
                                    <>
                                        <Box sx={{ mb: 2 }}>
                                            <Chip
                                                label={`${item.capacity_kw} kW`}
                                                color="primary"
                                                size="small"
                                                sx={{ mr: 1 }}
                                            />
                                            <Chip
                                                label={`Efficiency: ${item.efficiency}`}
                                                color="secondary"
                                                size="small"
                                            />
                                        </Box>
                                        <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                                            <span style={{ fontSize: '1rem', fontWeight: 'normal' }}>Cost: </span><span style={{ color: '#2e7d32' }}>€{item.cost_eur?.toLocaleString()}</span>
                                        </Typography>
                                    </>
                                )}

                                {selectedCategory === 1 && ( // Insulations
                                    <>
                                        <Box sx={{ mb: 2 }}>
                                            <Chip
                                                label={`${item.thickness_mm}mm`}
                                                color="primary"
                                                size="small"
                                                sx={{ mr: 1 }}
                                            />
                                            <Chip
                                                label={`R: ${item.r_value_m2K_per_W}`}
                                                color="secondary"
                                                size="small"
                                            />
                                        </Box>
                                        <Typography variant="body2" sx={{ mb: 1 }}>
                                            Material: {item.material}
                                        </Typography>
                                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                            <span style={{ fontSize: '1rem', fontWeight: 'normal' }}>Cost: </span><span style={{ color: '#2e7d32' }}>€{item.cost_per_sqm}/m²</span>
                                        </Typography>
                                    </>
                                )}

                                {selectedCategory === 2 && ( // PV Systems
                                    <>
                                        <Box sx={{ mb: 2 }}>
                                            <Chip
                                                label={`${item.kWp} kWp`}
                                                color="primary"
                                                size="small"
                                                sx={{ mr: 1 }}
                                            />
                                            <Chip
                                                label={`${item.panel_area} m²`}
                                                color="secondary"
                                                size="small"
                                            />
                                        </Box>
                                        <Typography variant="body2" sx={{ mb: 1 }}>
                                            Roof: {item.roof_area}m² | Terrace: {item.terrace_area}m²
                                        </Typography>
                                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                            <span style={{ fontSize: '1rem', fontWeight: 'normal' }}>Cost: </span><span style={{ color: '#2e7d32' }}>€{item.cost?.toLocaleString()}</span>
                                        </Typography>
                                    </>
                                )}

                                {selectedCategory === 3 && ( // PV + BESS
                                    <>
                                        <Box sx={{ mb: 2 }}>
                                            <Chip
                                                label={`${item.kWp} kWp`}
                                                color="primary"
                                                size="small"
                                                sx={{ mr: 1 }}
                                            />
                                            <Chip
                                                label={`${item.bess_capacity_kwh} kWh`}
                                                color="secondary"
                                                size="small"
                                            />
                                        </Box>
                                        <Typography variant="body2" sx={{ mb: 1 }}>
                                            Roof: {item.roof_area}m² | Terrace: {item.terrace_area}m²
                                        </Typography>
                                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                            <span style={{ fontSize: '1rem', fontWeight: 'normal' }}>Cost: </span><span style={{ color: '#2e7d32' }}>€{item.cost?.toLocaleString()}</span>
                                        </Typography>
                                    </>
                                )}

                                {selectedCategory === 4 && ( // Windows
                                    <>
                                        <Box sx={{ mb: 2 }}>
                                            <Chip
                                                label={`U: ${item.u_value}`}
                                                color="primary"
                                                size="small"
                                                sx={{ mr: 1 }}
                                            />
                                        </Box>
                                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                            <span style={{ fontSize: '1rem', fontWeight: 'normal' }}>Cost: </span><span style={{ color: '#2e7d32' }}>€{item.cost_per_sqm}/m²</span>
                                        </Typography>
                                    </>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            {/* Pagination */}
            {totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                    <Pagination
                        count={totalPages}
                        page={currentPage}
                        onChange={handlePageChange}
                        color="primary"
                        size="large"
                        showFirstButton
                        showLastButton
                    />
                </Box>
            )}
        </Container>
    );
};

export default DecarbonizationActions;